import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { normalizePlatformSettings } from '../lib/timezone';

const PlatformSettingsContext = createContext(null);

const PLATFORM_SETTINGS_DOC = 'global';

/**
 * Live platform-wide settings (`platformSettings/global`).
 * Subscribes only after auth is ready — avoids permission-denied noise
 * on public/landing routes before sign-in.
 */
export function PlatformSettingsProvider({ children }) {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || null);
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    if (!uid) {
      setRaw(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const ref = doc(db, 'platformSettings', PLATFORM_SETTINGS_DOC);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        setRaw(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      (err) => {
        // Permission errors are expected if rules lag; keep defaults quiet.
        if (err?.code !== 'permission-denied') {
          console.warn('PlatformSettingsProvider:', err);
        }
        setRaw(null);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [uid]);

  const settings = useMemo(() => normalizePlatformSettings(raw), [raw]);

  const value = useMemo(
    () => ({ settings, loading }),
    [settings, loading],
  );

  return (
    <PlatformSettingsContext.Provider value={value}>
      {children}
    </PlatformSettingsContext.Provider>
  );
}

const FALLBACK = {
  settings: normalizePlatformSettings(null),
  loading: false,
};

export function usePlatformSettings() {
  return useContext(PlatformSettingsContext) ?? FALLBACK;
}
